import {
  BoxV2 as Box,
  Button,
  Flex,
  FilterConfig,
  FilterType,
  Table,
  TableFilter,
  useFilters,
  useTable,
} from 'portal-commons';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  faFilter,
  faArrowDownToLine,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { DeclinedCnpCampaignListingRow } from '../components';
import { EmptyFetchedData } from '../constants';
import { CnpCampaignListItem } from '../types';
import { campaignController } from '@/apis/core';
import {
  getRejectionCategoryOptions,
  getTenDlcCnpListOptions,
  getTenDlcUseCaseOptions,
} from '@/apis/services';
import { PageTitle } from '@/components';
import { DateRangePresets } from '@/constants/options';
import {
  CLEAR_LOADING_MESSAGE,
  SET_LOADING_MESSAGE,
} from '@/shared_elements/actions';
import Loader from '@/shared_elements/containers/Loader';
import { toastFlashMessage } from '@/utils';
import { generateAndDownloadFile } from '@/utils/download';
import { processDateRangeQuery } from '@/utils/time';
import { MAX_RECORD_LIMIT } from '../../Brands/constants';

const FilterConfigs: Record<string, FilterConfig> = {
  campaignUid: {
    type: FilterType.Text,
    label: 'Campaign ID',
    placeholder: 'Enter campaign id',
    width: 150,
  },
  brandUid: {
    type: FilterType.Text,
    label: 'Brand ID',
    placeholder: 'Enter brand id',
    width: 150,
  },
  usecase: {
    type: FilterType.Dropdown,
    label: 'Use-Case',
    placeholder: 'Select use-case',
    options: [],
    width: 230,
  },
  downstreamCnpUid: {
    type: FilterType.Dropdown,
    label: 'Downstream CNP',
    placeholder: 'Select downstream CNP',
    options: [],
    width: 260,
  },
  sharedOn: {
    type: FilterType.DateRange,
    label: 'Shared With Me On',
    placeholder: 'Select date range',
    delayUpdate: false,
    dateRangePresets: DateRangePresets,
  },
  sharedOnUpdate: {
    type: FilterType.DateRange,
    label: 'Declined On',
    placeholder: 'Select date range',
    delayUpdate: false,
    dateRangePresets: DateRangePresets,
  },
  rejectionCategory: {
    type: FilterType.Dropdown,
    label: 'Rejection Category',
    placeholder: 'Select Rejection Category',
    options: [],
    multiple: true,
    width: 230,
  },
  status: {
    type: FilterType.Dropdown,
    label: 'TCR Status',
    placeholder: 'Select TCR status',
    options: [
      { label: 'Active', value: 'ACTIVE' },
      { label: 'Deactivated', value: 'EXPIRED' },
    ],
    width: 230,
  },
  provisional: {
    type: FilterType.Dropdown,
    label: 'CNP Migration',
    placeholder: 'Select CNP Migration',
    options: [
      { label: 'True', value: 'true' },
      { label: 'False', value: 'false' },
    ],
  },
};

const headRows = [
  { id: 'uid', label: 'CAMPAIGN ID', sortable: true },
  { id: 'brandUid', label: 'BRAND ID', sortable: true },
  { id: 'usecase', label: 'USE-CASE', sortable: false },
  { id: 'registeredOnDate', label: 'REGISTERED ON', sortable: true },
  { id: 'downstreamCnpName', label: 'DOWNSTREAM CNP', sortable: false },
  { id: 'sharedOnDate', label: 'SHARED WITH ME ON', sortable: true },
  { id: 'sharedOnUpdateDate', label: 'DECLINED ON', sortable: true },
  { id: 'description', label: 'DESCRIPTION', sortable: false },
  { id: 'status', label: 'TCR STATUS', sortable: false },
];

const DeclinedCnpCampaigns: FunctionComponent = () => {
  const {
    configs: filterConfigs,
    candidateFilters,
    appliedFilters,
    additionalFilters,
    handleEdit: handleFiltersEdit,
    handleApply: handleFiltersApply,
    setOptions: setFilterOptions,
  } = useFilters({
    configs: FilterConfigs,
  });
  const dispatch = useDispatch();
  const setLoadingMessage = (message: string) => {
    dispatch({ type: SET_LOADING_MESSAGE, payload: message });
  };
  const clearLoadingMessage = () => {
    dispatch({ type: CLEAR_LOADING_MESSAGE });
  };
  const [filtersShown, setFiltersShown] = useState(false);

  const fetchCampaigns = async () => {
    const queries = getQueries();
    const { provisional } = queries;
    if (provisional !== 'true' && provisional !== 'false') {
      return EmptyFetchedData;
    }

    const response = await campaignController.getCnpCampaigns({
      ...queries,
    });
    if (response) {
      return {
        data: response.records,
        rowsPerPage: response.recordsPerPage,
        total: response.totalRecords,
        page: response.page,
      };
    }
    return EmptyFetchedData;
  };

  const {
    loading: tableLoading,
    pageLoading,
    total: totalCampaigns,
    handleSorting: handleSortingChange,
    handleChangePage: handlePageChange,
    tableData: campaigns,
    pagination,
    filter: tableFilters,
  } = useTable<CnpCampaignListItem>(fetchCampaigns, {
    cappingTotal: 10000,
    deps: [appliedFilters, additionalFilters],
    defaultFilter: {
      sortField: 'sharedOnDate',
      ascendingOrder: false,
    },
  });

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    const { provisional } = appliedFilters;
    if (provisional !== 'true' && provisional !== 'false') {
      handleFiltersApply({
        ...appliedFilters,
        provisional: 'false',
      });
    }
  }, [appliedFilters.provisional]);

  const getQueries = () => {
    const queries = {
      sharingStatus: 'DECLINED',
      channelId: '10DLC',
      ...tableFilters,
      ...appliedFilters,
      ...additionalFilters,
    } as Record<string, any>;
    processDateRangeQuery(
      queries,
      'sharedOn',
      'sharedOnStartDate',
      'sharedOnEndDate'
    );
    processDateRangeQuery(
      queries,
      'sharedOnUpdate',
      'sharedOnUpdateStartDate',
      'sharedOnUpdateEndDate'
    );
    return queries;
  };

  const fetchFilterOptions = async () => {
    const [
      useCaseOptionsResult,
      cnpListOptionsResult,
      rejectionCategoryOptionsResult,
    ] = await Promise.allSettled([
      getTenDlcUseCaseOptions(),
      getTenDlcCnpListOptions(),
      getRejectionCategoryOptions(),
    ]);

    if (useCaseOptionsResult.status === 'fulfilled') {
      setFilterOptions('usecase', useCaseOptionsResult.value);
    }
    if (cnpListOptionsResult.status === 'fulfilled') {
      setFilterOptions('downstreamCnpUid', cnpListOptionsResult.value);
    }
    if (rejectionCategoryOptionsResult.status === 'fulfilled') {
      setFilterOptions(
        'rejectionCategory',
        rejectionCategoryOptionsResult.value
      );
    }
  };

  const handleDownload = async () => {
    setLoadingMessage('We are generating the document');
    const queries = getQueries();
    if ('page' in queries) {
      delete queries.page;
    }
    const response = await campaignController.downloadCnpCampaigns({
      ...queries,
    });
    if (response) {
      const file = new File([response], 'cnp-declined-campaigns.csv');
      generateAndDownloadFile(file);
    } else {
      toastFlashMessage('Failed to download event history', 'error');
    }
    clearLoadingMessage();
  };

  return (
    <>
      <Box
        as="section"
        sx={{
          // Fix dropdown width affected by global styling
          '& .MuiAutocomplete-root': {
            width: 'unset',
          },
        }}
        data-testid="campaignListing"
      >
        {pageLoading ? (
          <Loader />
        ) : (
          <Flex
            sx={{
              flexDirection: 'column',
              alignItems: 'flex-start',
              rowGap: '12px',
            }}
          >
            <PageTitle
              note={`Results capped at first ${MAX_RECORD_LIMIT.toLocaleString()} records`}
            >
              {totalCampaigns > 0
                ? `${totalCampaigns} 10DLC Declined Connectivity Partner Campaigns `
                : '10DLC Declined Connectivity Partner Campaigns'}
            </PageTitle>
            <Flex
              sx={{
                alignItems: 'center',
                columnGap: '12px',
                width: '100%',
                marginTop: '-8px',
              }}
            >
              <Button
                color="secondary"
                onClick={() => setFiltersShown(!filtersShown)}
                data-testid="tableAddFilterButton"
              >
                <FontAwesomeIcon icon={faFilter} />
                <span>{filtersShown ? 'Hide' : 'Show'} Filters</span>
              </Button>
              <Button
                variant="outline"
                color="secondary"
                onClick={handleDownload}
                data-testid="tableDownloadButton"
              >
                <FontAwesomeIcon icon={faArrowDownToLine} />
                Download
              </Button>
            </Flex>
            {filtersShown && (
              <TableFilter
                configs={filterConfigs}
                candidateValues={candidateFilters}
                appliedValues={appliedFilters}
                onCandidateValuesChange={handleFiltersEdit}
                onAppliedValuesChange={handleFiltersApply}
              />
            )}
            <Table
              testId="campaignsListingListingTableNew"
              loading={tableLoading}
              emptyState="no campaigns to view"
              handleChangePage={handlePageChange}
              createSortHandler={handleSortingChange}
              filter={tableFilters}
              headRows={headRows}
              data={campaigns as Record<string, any>[]}
              rowKey="uid"
              renderRow={(data) => {
                const campaign = data as unknown as CnpCampaignListItem;
                return (
                  <DeclinedCnpCampaignListingRow
                    key={campaign.uid}
                    campaign={campaign}
                    rejectionCategoryOptions={
                      filterConfigs.rejectionCategory.options
                    }
                  />
                );
              }}
              pagination={pagination}
            />
          </Flex>
        )}
      </Box>
    </>
  );
};

export default DeclinedCnpCampaigns;
